import apiClient from './axios_config';

import * as url from './url_helper';

const unwrapData = <T = any>(request: Promise<any>): Promise<T> =>
  request.then((response: any) => response?.data as T);

export const callLogin = (params?: any) => unwrapData(apiClient.post(url.LOGIN, params));

export const callGetAccount = () => unwrapData(apiClient.get(url.ACCOUNT));
export const callChangePassword = (param: { currentPassword: string; newPassword: string }) =>
  unwrapData(apiClient.post(url.ACCOUNT_CHANGE_PASSWORD, param));
export const callSwitchTenant = (param: any) =>
  unwrapData(apiClient.post(`${url.ACCOUNT}/switch-tenant`, param));

export const callGetPublicTenantsByChain = (chainName: string) =>
  unwrapData(apiClient.get(url.PUBLIC_TENANTS_BY_CHAIN, { chainName }));

export const callGetShift = (param?: any) => unwrapData(apiClient.get(url.SHIFT, param));
export const callCreateShift = (param: any) => unwrapData(apiClient.post(url.SHIFT, param));
export const callUpdateShift = (param: any) => unwrapData(apiClient.put(`${url.SHIFT}/${param.id}`, param));
export const callDeleteShift = (id: number) => unwrapData(apiClient.delete(`${url.SHIFT}/${id}`));
export const callGetShiftDetail = (id: string) => unwrapData(apiClient.get(`${url.SHIFT}/${id}`));
export const callGetFinancialShiftDetail = (id: string) =>
  unwrapData(apiClient.get(`${url.SHIFT}/${id}/detail`));
export const callGetPreviousShiftDetail = (startTime: string) =>
  unwrapData(apiClient.get(`${url.SHIFT}/previous-detail`, { startTime })).catch((error: any) => {
    if (error?.response?.status === 404) return null;
    throw error;
  });
export const callDraftFinancialShiftDetail = (id: string, param?: any) =>
  unwrapData(apiClient.patch(`${url.SHIFT}/${id}/close`, param));

export const callGetAdminUsers = (params?: any) => apiClient.get(url.ADMIN_USER, params);
export const callCreateAdminUser = (param: any) => unwrapData(apiClient.post(url.ADMIN_USER, param));
export const callUpdateAdminUser = (param: any) => unwrapData(apiClient.put(url.ADMIN_USER, param));
export const callPatchAdminUser = (
  userId: number,
  param: { firstName?: string; lastName?: string; email?: string; activated?: boolean },
) => unwrapData(apiClient.patch(`${url.ADMIN_USER}/${userId}`, param));
export const callPatchAdminUserAuthorities = (userId: number, param: { authorities: string[] }) =>
  unwrapData(apiClient.patch(`${url.ADMIN_USER}/${userId}/authorities`, param));
export const callChangeUserPassword = (userId: number, param: { newPassword: string }) =>
  unwrapData(apiClient.post(`${url.ADMIN_USER}/${userId}/change-password`, param));

export const callGetUsers = () => unwrapData(apiClient.get(url.USER));
export const callGetAuthorities = (params?: any) => unwrapData(apiClient.get(url.AUTHORITY, params));
export const callCreateAuthority = (authorityName: string) =>
  unwrapData(apiClient.post(url.AUTHORITY, { name: authorityName }));
export const callGetPermissionFeatures = () => unwrapData(apiClient.get(url.PERMISSION_FEATURE));
export const callGetAuthorityPermissions = (authority: string) =>
  unwrapData(apiClient.get(`${url.PERMISSION_AUTHORITY}/${authority}`));
export const callPatchAuthorityPermissions = (
  authority: string,
  param: { permissions: Array<{ feature: string; permissionMask: number }> },
) => unwrapData(apiClient.patch(`${url.PERMISSION_AUTHORITY}/${authority}`, param));
export const callGetUserPermissions = (userId: number) =>
  unwrapData(apiClient.get(`${url.PERMISSION_USER}/${userId}`));
export const callPatchUserPermissions = (
  userId: number,
  param: { permissions: Array<{ feature: string; permissionMask: number }> },
) => unwrapData(apiClient.patch(`${url.PERMISSION_USER}/${userId}`, param));

export const callGetCustomers = (params?: any) => apiClient.get(url.CUSTOMER, params);
export const callSearchCustomersWithObjects = (params?: any) =>
  apiClient.get(`${url.CUSTOMER}/search-with-objects`, params);
export const callGetCustomerDebtReport = (params?: any) =>
  unwrapData(apiClient.get(url.CUSTOMER_DEBT_REPORT, params));
export const callExportDebtReport = (params?: any) =>
  apiClient.get(url.DEBT_REPORT_EXPORT, params, { responseType: 'blob' });
export const callExportOrderList = (params?: any) =>
  apiClient.get(url.ORDER_LIST_EXPORT, params, { responseType: 'blob' });
export const callExportPaymentList = (params?: any) =>
  apiClient.get(url.PAYMENT_LIST_EXPORT, params, { responseType: 'blob' });
export const callGetCustomerDetail = (id: string | number) =>
  unwrapData(apiClient.get(`${url.CUSTOMER}/${id}`));
export const callAddCustomer = (param: any) => unwrapData(apiClient.post(url.CUSTOMER, param));
export const callEditCustomer = (id: string, param: any) =>
  unwrapData(apiClient.put(`${url.CUSTOMER}/${id}`, param));

export const callGetCustomerObject = (param?: any) => apiClient.get(url.CUSTOMER_OBJECT, { isActive: true, ...param });
export const callGetCustomerObjectsByCustomerIds = (customerIds: Array<string | number>, param?: any) =>
  apiClient.get(`${url.CUSTOMER_OBJECT}/by-customer-ids`, {
    isActive: true,
    ...param,
    customerIds: customerIds.join(','),
  });
export const callGetCustomerObjectById = (id: string) =>
  unwrapData(apiClient.get(`${url.CUSTOMER_OBJECT}/${id}`));
export const callCreateCustomerObject = (param: any) => unwrapData(apiClient.post(url.CUSTOMER_OBJECT, param));
export const callUpdateCustomerObject = (param: any) =>
  unwrapData(apiClient.put(`${url.CUSTOMER_OBJECT}/${param.id}`, param));
export const callDeleteCustomerObject = (id?: number) =>
  unwrapData(apiClient.patch(`${url.CUSTOMER_OBJECT}/${id}`));

export const callGetPresignFromS3 = (param?: any) => unwrapData(apiClient.post(url.S3_PRESIGN_UPLOAD, param));
export const callDownloadImage = (param?: any) => unwrapData(apiClient.post(url.S3_PRESIGN_DOWNLOAD, param));
export const callDownloadImages = (param?: any) =>
  unwrapData(apiClient.post(url.S3_PRESIGN_DOWNLOAD_BATCH, param));

export const callGetOrders = (param?: any) => apiClient.get(url.ORDER, param);
export const callGetOrderDetail = (id: number) => unwrapData(apiClient.get(`${url.ORDER}/${id}`));
export const callCreateOrder = (param?: any) => unwrapData(apiClient.post(`${url.ORDER}/create`, param));
export const callUpdateOrder = (id: number, param?: any) =>
  unwrapData(apiClient.put(`${url.ORDER}/${id}`, param));
export const callDeleteOrder = (id: number) => unwrapData(apiClient.delete(`${url.ORDER}/${id}`));

export const callGetServices = (param?: any) => unwrapData(apiClient.get(url.SERVICE, param));
export const callGetServiceById = (id: string | number) => unwrapData(apiClient.get(`${url.SERVICE}/${id}`));
export const callBatchUpdateServicePrice = (param: {
  items: Array<{ id: number; price: number }>;
}) => unwrapData(apiClient.patch(url.SERVICE_BATCH_PRICE, param));

export const callGetServiceCategories = (param?: any) => apiClient.get(url.SERVICE_CATEGORY, param);
export const callGetServiceCategoryById = (id: string | number) =>
  unwrapData(apiClient.get(`${url.SERVICE_CATEGORY}/${id}`));
export const callCreateServiceCategory = (param: any) =>
  unwrapData(apiClient.post(url.SERVICE_CATEGORY, param));
export const callUpdateServiceCategory = (id: number, param: any) =>
  unwrapData(apiClient.patch(`${url.SERVICE_CATEGORY}/${id}`, { ...param, id }));
export const callDeleteServiceCategory = (id: number) =>
  unwrapData(apiClient.delete(`${url.SERVICE_CATEGORY}/${id}`));

export const callCreateService = (param: any) => unwrapData(apiClient.post(url.SERVICE, param));
export const callUpdateService = (id: number, param: any) =>
  unwrapData(apiClient.patch(`${url.SERVICE}/${id}`, { ...param, id }));
export const callDeleteService = (id: number) => unwrapData(apiClient.delete(`${url.SERVICE}/${id}`));
export const callGetServicePrice = (param: any) => unwrapData(apiClient.get(`${url.SERVICE}/price-at`, param));

export const callGetReceipts = (param: any) => apiClient.get(url.RECEIPT, param);
export const callCreateReceipt = (param: any) => unwrapData(apiClient.post(url.RECEIPT, param));
export const callUpdateReceipt = (id: number, param: any) =>
  unwrapData(apiClient.patch(`${url.RECEIPT}/${id}`, { ...param, id }));
export const callDeleteReceipt = (id: number) => unwrapData(apiClient.delete(`${url.RECEIPT}/${id}`));
export const callGetReceiptPaymentReport = (param: any) =>
  unwrapData(apiClient.get(url.RECEIPT_PAYMENT_REPORT, param));
export const callGetAllReceiptPaymentReport = async (param: any) => {
  const size = 1000;
  const items: any[] = [];
  let page = 0;
  let response: any = null;

  do {
    response = await callGetReceiptPaymentReport({ ...param, page, size });
    const pageItems = Array.isArray(response?.items) ? response.items : [];
    items.push(...pageItems);
    if (pageItems.length < size) break;
    page += 1;
  } while (true);

  return { ...(response || {}), items };
};

export const callGetCustomerVoucherOverview = (param?: any) =>
  apiClient.get(`${url.CUSTOMER_VOUCHER}/overviews`, param);
export const callDeleteCustomerVoucher = (id: number) =>
  unwrapData(apiClient.delete(`${url.CUSTOMER_VOUCHER}/${id}`));
export const callUseCustomerVoucher = (param: any) =>
  unwrapData(apiClient.post(url.CUSTOMER_VOUCHER_USE, param));
export const callGetCustomerObjectVoucherTxns = (param?: any) =>
  apiClient.get(url.CUSTOMER_OBJECT_VOUCHER_TXN, param);
export const callDeleteCustomerObjectVoucherTxn = (id: number) =>
  unwrapData(apiClient.delete(`${url.CUSTOMER_OBJECT_VOUCHER_TXN}/${id}`));

export const callGetPublicCustomerVoucher = (param?: any) =>
  unwrapData(apiClient.get(url.PUBLIC_CUSTOMER_VOUCHER, param));

export const callGetExpenseCategories = (param?: any) => apiClient.get(url.EXPENSE_CATEGORY, param);
export const callCreateExpenseCategory = (param: any) =>
  unwrapData(apiClient.post(url.EXPENSE_CATEGORY, param));
export const callUpdateExpenseCategory = (id: number, param: any) =>
  unwrapData(apiClient.put(`${url.EXPENSE_CATEGORY}/${id}`, { ...param, id }));
export const callDeleteExpenseCategory = (id: number) =>
  unwrapData(apiClient.delete(`${url.EXPENSE_CATEGORY}/${id}`));

export const callGetExpenseVouchers = (param?: any) => apiClient.get(url.EXPENSE_VOUCHER, param);
export const callCreateExpenseVoucher = (param: any) => unwrapData(apiClient.post(url.EXPENSE_VOUCHER, param));
export const callDeleteExpenseVoucher = (id: number) =>
  unwrapData(apiClient.delete(`${url.EXPENSE_VOUCHER}/${id}`));

export const callApproveExpenseVoucher = (id: number, param?: any) =>
  unwrapData(apiClient.post(`${url.EXPENSE_VOUCHER}/${id}/approval`, param || {}));

export const callGetProductCategories = (param?: any) => apiClient.get(url.PRODUCT_CATEGORY, param);
export const callCreateProductCategory = (param: any) =>
  unwrapData(apiClient.post(url.PRODUCT_CATEGORY, param));
export const callUpdateProductCategory = (id: number, param: any) =>
  unwrapData(apiClient.patch(`${url.PRODUCT_CATEGORY}/${id}`, { ...param, id }));
export const callDeleteProductCategory = (id: number) =>
  unwrapData(apiClient.delete(`${url.PRODUCT_CATEGORY}/${id}`));
export const callCreateProduct = (param: any) => unwrapData(apiClient.post(url.PRODUCT, param));
export const callUpdateProduct = (id: number, param: any) =>
  unwrapData(apiClient.patch(`${url.PRODUCT}/${id}`, { ...param, id }));
export const callDeleteProduct = (id: number) => unwrapData(apiClient.delete(`${url.PRODUCT}/${id}`));

export const callCreateInventoryDoc = (param: any) => unwrapData(apiClient.post(url.INVENTORY_DOC, param));
export const callGetInventoryDocs = (param?: any) => apiClient.get(url.INVENTORY_DOC, param);
export const callGetInventoryDocById = (id: number | string) =>
  unwrapData(apiClient.get(`${url.INVENTORY_DOC}/${id}`));
export const callUpdateInventoryDoc = (id: number | string, param: any) =>
  unwrapData(apiClient.patch(`${url.INVENTORY_DOC}/${id}`, { ...param, id }));
export const callDeleteInventoryDoc = (id: number | string) =>
  unwrapData(apiClient.delete(`${url.INVENTORY_DOC}/${id}`));
export const callGetInventoryStockReport = (param?: any) =>
  unwrapData(apiClient.get(url.INVENTORY_STOCK_REPORT, param));
export const callExportStockReportDetail = (params?: any) =>
  apiClient.get(url.STOCK_REPORT_EXPORT_DETAIL, params, { responseType: 'blob' });

export const callGetPumps = (param?: any) => apiClient.get(url.PUMP, param);
export const callCreatePump = (param: any) => unwrapData(apiClient.post(url.PUMP, param));
export const callUpdatePump = (param: any) => unwrapData(apiClient.put(`${url.PUMP}/${param.id}`, param));
export const callDeletePump = (id: number) => unwrapData(apiClient.delete(`${url.PUMP}/${id}`));
export const callGetPumpDetail = (id: number) => unwrapData(apiClient.get(`${url.PUMP}/${id}`));
export const callGetAuditLogs = ({ entityType, recordId, ...params }: any) => {
  const baseUrl = recordId
    ? `${url.AUDIT_LOG}/${entityType}/${recordId}/history`
    : `${url.AUDIT_LOG}/${entityType}/history`;

  return apiClient.get(baseUrl, params);
};
